import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProduits, useUsines } from "@/lib/donnees";
import { Kpi, Kpis, Section, Tag } from "@/ui/primitives";
import { Table, dansUnChamp } from "@/ui/Table";
import { entier } from "@/lib/format";
import type { Usine } from "@/types/metier";

export function Usines() {
  const navigue = useNavigate();
  const { donnees: usines, chargement, erreur } = useUsines();
  const { donnees: produits } = useProduits();

  const refs = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of produits) m.set(p.usine_id, (m.get(p.usine_id) ?? 0) + 1);
    return m;
  }, [produits]);

  const tarifes = produits.filter((p) => (p.prix ?? 0) > 0).length;

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <div className="pad">
      <Kpis>
        <Kpi k="Usines" v={entier(usines.length)} n="partenaires actifs" />
        <Kpi k="Références" v={entier(produits.length)} n="toutes usines" />
        <Kpi k="Références tarifées" v={entier(tarifes)} n="prêtes à être proposées" />
      </Kpis>

      <Section titre="Usines" note={chargement ? "chargement…" : `${usines.length} fiches`}>
        <Table
          lignes={usines}
          cle={(u: Usine) => u.id}
          onClic={(u, ev) => {
            if (!dansUnChamp(ev)) navigue(`/usines/${u.id}`);
          }}
          colonnes={[
            { cle: "nom", titre: "Usine", filtre: "texte",
              sous: (u) => [u.ville, u.pays].filter(Boolean).join(", ") || "—" },
            { cle: "categorie", titre: "Catégorie", filtre: "select", mobile: false },
            { cle: "ville", titre: "Ville", mobile: false },
            { cle: "pays", titre: "Pays", filtre: "select", mobile: false },
            { cle: "prefixe_facture", titre: "Préfixe facture", court: "Préfixe",
              type: "ref", mobile: false },
            { cle: "refs", titre: "Références", court: "Réfs", type: "num", total: true,
              nombre: (u) => refs.get(u.id) ?? 0 },
            { cle: "couleur", titre: "Couleur", mobile: false,
              rendu: (u) => (
                <span className="couleur-champ">
                  <span
                    className="logo-tuile"
                    style={{ width: "18px", height: "18px", background: u.couleur }}
                  />
                  <span className="mono">{u.couleur}</span>
                </span>
              ) },
            { cle: "actif", titre: "État",
              texte: (u) => (u.actif ? "Active" : "Inactive"),
              rendu: (u) => (u.actif ? <Tag ton="ok" dot>active</Tag> : <Tag dot>inactive</Tag>) },
          ]}
        />
      </Section>
    </div>
  );
}
